import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import Modal from 'react-modal';
import CallToActionBanner from '../components/CallToActionBanner';
import Subject from './Subject';
import { useStores } from '../hooks/use-stores';
import { ThemedButton } from '@awarego-ui/components';
import { useFetch } from 'use-http';
import PreviewModalHeader from './previewModalHeader';
import ThemedSpinner from '../components/themed/ThemedSpinner';
import {
    getPreviewLanguageOptions,
    resolveHraPreviewLanguage,
    setStoredHraPreviewLanguage,
} from '../utils/hraPreviewLanguage';

/**
 * Fetches subject content for a resolved language.
 * Mounted only after language is known so we never race en+it requests
 * (use-http does not abort the previous in-flight request).
 */
function SubjectPreviewBody({
    subjectId,
    selectedLanguage,
    partnerId,
    companyId,
    currentCompany,
    xapi,
}) {
    const subjectsPath = partnerId
        ? `/partners/${partnerId}/subjects/${subjectId}?lang=${selectedLanguage}`
        : `/companies/${companyId}/subjects/${subjectId}?lang=${selectedLanguage}`;

    const {
        loading,
        error,
        data = null,
    } = useFetch(subjectsPath, { cachePolicy: 'no-cache' }, [
        currentCompany,
        selectedLanguage,
        subjectId,
        partnerId,
    ]);

    if (loading || !data || error) {
        return <ThemedSpinner />;
    }

    return (
        <Subject
            subject={data}
            showBackLink={false}
            language={selectedLanguage}
            companyId={companyId}
            xapi={xapi}
        />
    );
}

function SubjectPreviewContainer({
    isOpen,
    onClose,
    subjectId,
    redirectToUpgrade,
    hideSubjectsModal,
    hideFooter,
    showLanguageSelector,
    availableLanguages,
    defaultLanguage = 'en',
    subjectTitle = null,
    xapi = false,
    partnerId = null,
    companyId = null,
}) {
    const { store, catalogStore, brandingStore } = useStores();
    const { currentCompany, havePartnerSubscription } = store;
    const effectiveCompanyId = companyId || currentCompany?.company_id;
    const subjectFromCatalog = catalogStore.allSubjectsIndex[subjectId];
    const companyLanguages = currentCompany?.languages;

    // null until resolved against company-enabled languages
    const [selectedLanguage, setSelectedLanguage] = useState(null);

    const previewLanguages = useMemo(() => {
        const enabled = companyLanguages
            ? companyLanguages
                  .filter((lang) => lang.enabled)
                  .map((lang) => lang.code)
            : [];
        return getPreviewLanguageOptions({
            contentLanguages: availableLanguages,
            enabledLanguages: enabled,
            companyLanguages,
            companyId: effectiveCompanyId,
            fallback: defaultLanguage,
        });
    }, [
        availableLanguages,
        effectiveCompanyId,
        companyLanguages,
        defaultLanguage,
    ]);

    const previewLanguagesKey = previewLanguages.join(',');

    const selectLanguage = useCallback(
        (languageId) => {
            store.resetVideoTokens();
            setSelectedLanguage(languageId);
            setStoredHraPreviewLanguage(languageId);
        },
        [store]
    );

    useEffect(() => {
        if (!previewLanguages.length) {
            setSelectedLanguage(null);
            return;
        }
        const resolved = resolveHraPreviewLanguage(
            previewLanguages,
            defaultLanguage
        );
        selectLanguage(resolved);
        // previewLanguagesKey tracks content; avoid re-firing on new array identity
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectId, previewLanguagesKey, defaultLanguage, selectLanguage]);

    const { supportEmail } = brandingStore;
    const previewTitle =
        subjectTitle || (subjectFromCatalog && subjectFromCatalog.title);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="preview-questions-modal"
        >
            <PreviewModalHeader
                titlePrefix={'Preview:'}
                title={previewTitle}
                showLanguageSelector={showLanguageSelector}
                onSelectLanguage={selectLanguage}
                selectedLanguage={selectedLanguage || defaultLanguage}
                availableLanguages={previewLanguages}
                onClose={onClose}
            />
            {subjectFromCatalog && subjectFromCatalog.disabled && (
                <CallToActionBanner
                    isPartnerCompany={havePartnerSubscription}
                    supportEmail={supportEmail}
                    redirectToUpgrade={() => {
                        redirectToUpgrade && redirectToUpgrade();
                        onClose();
                        hideSubjectsModal && hideSubjectsModal();
                    }}
                />
            )}
            {selectedLanguage != null ? (
                <SubjectPreviewBody
                    key={`${subjectId}:${selectedLanguage}`}
                    subjectId={subjectId}
                    selectedLanguage={selectedLanguage}
                    partnerId={partnerId}
                    companyId={effectiveCompanyId}
                    currentCompany={currentCompany}
                    xapi={xapi}
                />
            ) : (
                <ThemedSpinner />
            )}
            {!hideFooter && (
                <div className={'modalFooter'}>
                    <ThemedButton
                        mode="primary"
                        onClick={onClose}
                        text="Back to add subjects"
                    />
                </div>
            )}
        </Modal>
    );
}

export default observer(SubjectPreviewContainer);
